import React, { useState, Fragment, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { useQueryClient } from '@tanstack/react-query';
import { Settings } from 'lucide-react';
import { useTranslation } from '../../context/TranslationContext';
import { scrollToFirstErrorField } from '../../utils/scrollToFirstError';
import { ValidationError } from '../../admin/components/ValidationError';
import { translateWithFallback } from '../../utils/translateWithFallback';
import { checkUsernameAvailable, updateUserProfile } from '../../services/api.shop';
import { UpdateUserProfileDto, UserProfileDto } from '../../types';
import {
    getUsernameDisallowMessage,
    isDisallowedUsername,
    isUsernameTooShort,
    publicProfileHeading,
} from '../../utils/usernameRules';
import AccountHealthBadge from './AccountHealthBadge';

const norm = (v: string | null | undefined) => (v ?? '').trim();

interface AccountProfileCardProps {
    profile: UserProfileDto;
    /** Own public shop window (/users/:id). */
    publicProfileUserId?: string;
    /** When set, Settings opens the account settings surface instead of the edit modal. */
    onOpenSettings?: () => void;
    /** Highlight Settings when the settings surface is open. */
    settingsActive?: boolean;
}

const AccountProfileCard: React.FC<AccountProfileCardProps> = ({
    profile,
    publicProfileUserId,
    onOpenSettings,
    settingsActive,
}) => {
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const queryClient = useQueryClient();
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [formData, setFormData] = useState<UpdateUserProfileDto>({
        userName: '',
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
        shippingStreetAddress: '',
        shippingCity: '',
        shippingPostalCode: '',
        shippingCountry: '',
        taxId: '',
        profilePictureUrl: '',
    });
    const [formError, setFormError] = useState<string | null>(null);

    useEffect(() => {
        setFormData({
            userName: profile.userName || '',
            currentPassword: '',
            newPassword: '',
            confirmNewPassword: '',
            shippingStreetAddress: profile.shippingStreetAddress || '',
            shippingCity: profile.shippingCity || '',
            shippingPostalCode: profile.shippingPostalCode || '',
            shippingCountry: profile.shippingCountry || '',
            taxId: profile.taxId || '',
            profilePictureUrl: profile.profilePictureUrl || '',
        });
    }, [profile]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const isDirty = useMemo(() => {
        const passwordTouched =
            !!formData.currentPassword || !!formData.newPassword || !!formData.confirmNewPassword;
        return (
            passwordTouched ||
            norm(formData.userName) !== norm(profile.userName) ||
            // profile picture URL self-serve disabled (unused / moderation)
            // norm(formData.profilePictureUrl) !== norm(profile.profilePictureUrl) ||
            norm(formData.shippingStreetAddress) !== norm(profile.shippingStreetAddress) ||
            norm(formData.shippingCity) !== norm(profile.shippingCity) ||
            norm(formData.shippingPostalCode) !== norm(profile.shippingPostalCode) ||
            norm(formData.taxId) !== norm(profile.taxId)
        );
    }, [formData, profile]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isDirty) return;
        if (formData.newPassword && formData.newPassword !== formData.confirmNewPassword) {
            setFormError(tr('account.profile.errors.passwordmismatch', 'Passwords do not match'));
            scrollToFirstErrorField();
            return;
        }
        if (formData.newPassword && !formData.currentPassword) {
            setFormError(tr('account.profile.errors.currentpasswordrequired', 'Current password is required'));
            scrollToFirstErrorField();
            return;
        }
        setFormError(null);
        const usernameChanged =
            norm(formData.userName).toLowerCase() !== norm(profile.userName).toLowerCase();
        if (usernameChanged && formData.userName && (isUsernameTooShort(formData.userName) || isDisallowedUsername(formData.userName))) {
            setFormError(
                getUsernameDisallowMessage(formData.userName) ||
                    tr('account.profile.errors.username', 'This username is not allowed')
            );
            scrollToFirstErrorField();
            return;
        }
        if (usernameChanged && formData.userName) {
            try {
                const data = await checkUsernameAvailable(formData.userName.trim());
                if (!data.available) {
                    setFormError(
                        getUsernameDisallowMessage(formData.userName) ||
                            tr('account.profile.errors.usernameTaken', 'Username is already taken')
                    );
                    scrollToFirstErrorField();
                    return;
                }
            } catch {
                /* backend SetUserNameAsync still enforces uniqueness */
            }
        }
        try {
            // Belgium-only shipping (SiteSettings defaultCountryCode = BE)
            // profilePictureUrl omitted — self-serve image URL disabled
            const { profilePictureUrl: _omitPic, ...rest } = formData;
            await updateUserProfile({ ...rest, shippingCountry: 'BE' });
            setIsEditModalOpen(false);
            await queryClient.invalidateQueries({ queryKey: ['userProfile'] });
            await queryClient.invalidateQueries({ queryKey: ['user'] });
            await queryClient.invalidateQueries({ queryKey: ['products'] });
            await queryClient.invalidateQueries({ queryKey: ['search'] });
        } catch {
            setFormError(tr('account.profile.errors.updatefailed', 'Failed to update profile'));
            scrollToFirstErrorField();
        }
    };

    const heading = publicProfileHeading({
        userName: profile.userName,
        firstName: profile.firstName,
        lastName: profile.lastName,
    });
    const initials = (heading.title || profile.email || '?')
        .replace(/^@/, '')
        .charAt(0)
        .toUpperCase();

    return (
        <>
            <div className="relative mb-6 overflow-hidden rounded-2xl bg-[color-mix(in_srgb,var(--bg-color),white_15%)] shadow-md">
                <div className="p-6 pb-14">
                    <div className="flex items-start gap-4">
                        {profile.profilePictureUrl ? (
                            <img
                                src={profile.profilePictureUrl}
                                alt={heading.title}
                                className="h-20 w-20 rounded-full object-cover ring-4 ring-[color-mix(in_srgb,var(--accent-color),transparent_60%)]"
                            />
                        ) : (
                            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[var(--accent-color)] text-2xl font-bold text-white shadow-sm">
                                {initials}
                            </div>
                        )}
                        <div className="min-w-0 flex-1">
                            <h2 className="text-xl font-bold text-[var(--text-color)]">{heading.title}</h2>
                            {heading.subtitle && (
                                <p className="truncate text-sm text-[var(--text-color)]">{heading.subtitle}</p>
                            )}
                            <p className="text-sm text-[color-mix(in_srgb,var(--text-color),transparent_35%)]">{profile.email}</p>
                            <p className="mt-1 text-xs text-[color-mix(in_srgb,var(--text-color),transparent_50%)]">
                                {tr('account.member', 'Petito member')}
                            </p>
                        </div>
                        <button
                            type="button"
                            aria-pressed={settingsActive ? true : undefined}
                            onClick={() => (onOpenSettings ? onOpenSettings() : setIsEditModalOpen(true))}
                            className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                                settingsActive
                                    ? 'border-[var(--accent-color)] bg-[var(--accent-color)] text-white shadow-sm'
                                    : 'border-[var(--accent-color)] text-[var(--text-color)] hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_80%)]'
                            }`}
                        >
                            <Settings className="h-4 w-4" />
                            {tr('account.profile.editsettings', 'Edit settings')}
                        </button>
                    </div>
                </div>
                {/* Footer row: public profile (left) ↔ standing (right) */}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 px-3 pb-3 sm:px-4 sm:pb-4">
                    {publicProfileUserId ? (
                        <Link
                            to={`/users/${publicProfileUserId}`}
                            className="pointer-events-auto inline-flex min-h-11 max-w-[55%] items-center text-sm font-medium text-[var(--accent-color)] hover:underline"
                        >
                            {tr('profile.view.public', 'View public profile')}
                        </Link>
                    ) : (
                        <span />
                    )}
                    <div className="pointer-events-auto shrink-0">
                        <AccountHealthBadge />
                    </div>
                </div>
            </div>

            <Transition appear show={isEditModalOpen} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={() => setIsEditModalOpen(false)}>
                    <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black/25" />
                    </Transition.Child>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-[var(--bg-color)] p-0 text-left align-middle shadow-xl transition-all">
                                    <div className="px-6 pt-6 pb-2 flex items-center justify-between border-b">
                                        <Dialog.Title as="h3" className="text-lg font-medium text-[var(--text-color)]">
                                            {tr('account.profile.edit', 'Edit profile')}
                                        </Dialog.Title>
                                        <button type="button" onClick={() => setIsEditModalOpen(false)} className="text-xl leading-none">×</button>
                                    </div>

                                    {/* Simple tabs matching Figma edit profile — only Personal active for now */}
                                    <div className="flex border-b px-6 text-sm">
                                        <button type="button" className="px-4 py-2 border-b-2 border-[var(--accent-color)] text-[var(--accent-color)] font-medium">Personal</button>
                                        {/* <button type="button" className="px-4 py-2 text-[var(--text-color)]/60">Contact</button> */}
                                        {/* <button type="button" className="px-4 py-2 text-[var(--text-color)]/60">About</button> */}
                                        {/* <button type="button" className="px-4 py-2 text-[var(--text-color)]/60">Privacy</button> */}
                                    </div>

                                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-[var(--text-color)]">{tr('account.profile.username', 'Username')}</label>
                                            <input name="userName" value={formData.userName} onChange={handleInputChange} className="w-full rounded border p-2" />
                                        </div>
                                        {/* Profile picture URL — disabled (unused / moderation risk)
                                        <div>
                                            <label>{tr('account.profile.picture', 'Profile picture URL')}</label>
                                            <input name="profilePictureUrl" value={formData.profilePictureUrl || ''} onChange={handleInputChange} className="w-full rounded border p-2" />
                                        </div>
                                        */}
                                        <div>
                                            <label className="block text-sm font-medium text-[var(--text-color)]">{tr('account.profile.password.current', 'Current password')}</label>
                                            <input type="password" name="currentPassword" value={formData.currentPassword} onChange={handleInputChange} className="w-full rounded border p-2" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-[var(--text-color)]">{tr('account.profile.password.new', 'New password')}</label>
                                            <input type="password" name="newPassword" value={formData.newPassword} onChange={handleInputChange} className="w-full rounded border p-2" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-[var(--text-color)]">{tr('account.profile.password.confirm', 'Confirm new password')}</label>
                                            <input type="password" name="confirmNewPassword" value={formData.confirmNewPassword} onChange={handleInputChange} className="w-full rounded border p-2" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-[var(--text-color)]">{tr('account.profile.shippingaddress.street', 'Street')}</label>
                                            <input name="shippingStreetAddress" value={formData.shippingStreetAddress} onChange={handleInputChange} className="w-full rounded border p-2" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-[var(--text-color)]">{tr('account.profile.shippingaddress.city', 'City')}</label>
                                            <input name="shippingCity" value={formData.shippingCity} onChange={handleInputChange} className="w-full rounded border p-2" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-[var(--text-color)]">{tr('account.profile.shippingaddress.postalcode', 'Postal code')}</label>
                                            <input name="shippingPostalCode" value={formData.shippingPostalCode} onChange={handleInputChange} className="w-full rounded border p-2" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-[var(--text-color)]">{tr('account.profile.shippingaddress.country', 'Country')}</label>
                                            <input
                                                name="shippingCountry"
                                                value="Belgium (BE)"
                                                readOnly
                                                disabled
                                                className="w-full cursor-not-allowed rounded border p-2 opacity-80"
                                            />
                                        </div>
                                        {profile.isFlagged && !profile.hasProvidedTaxInfo && (
                                            <div>
                                                <p className="mb-2 text-red-500">{tr('account.profile.taxinfo', 'Tax information required')}</p>
                                                <input type="text" name="taxId" value={formData.taxId || ''} onChange={handleInputChange} className="w-full rounded border p-2" required />
                                            </div>
                                        )}
                                        <ValidationError error={formError ?? undefined} />
                                        <div className="flex justify-end space-x-2">
                                            <button type="button" onClick={() => setIsEditModalOpen(false)} className="rounded border border-[var(--accent-color)] px-4 py-2 text-[var(--text-color)] hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_80%)]">{tr('common.cancel', 'Cancel')}</button>
                                            <button
                                                type="submit"
                                                disabled={!isDirty}
                                                className="min-h-11 rounded bg-[var(--accent-color)] px-4 py-2 text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                {tr('state.save', 'Save')}
                                            </button>
                                        </div>
                                    </form>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </>
    );
};

export default AccountProfileCard;